/**
 * useLessonsOffered — does this club offer lessons, and which kind?
 *
 * Decides whether the Lessons ENTRY POINTS render: "Book a Lesson" needs a
 * bookable instructor, "Lesson Packages" needs a listed package. They are read
 * separately because a club can add instructors without ever selling a package.
 * The routes stay mounted; a deep link lands on the page's own empty state.
 *
 * Derived from the club's data, not a flag: most clubs will never offer lessons.
 * Both reads are the queries (and keys) the Lessons pages use, so those open warm.
 * A refusal or an error counts as "none": an entry that opens onto a failure is
 * worse than no entry.
 */

import { useClubId } from '~/services/appRuntime';
import { useLessons } from './useLessons';
import { useClubInstructors } from './useClubInstructors';

export interface LessonsOffered {
  /** The club lists at least one lesson package this caller can see. */
  hasPackages: boolean;
  /** The club has at least one bookable instructor for a direct lesson. */
  hasInstructors: boolean;
}

export function useLessonsOffered(): LessonsOffered {
  const [clubIdParam] = useClubId();
  const clubId = clubIdParam ? Number(clubIdParam) : undefined;

  const packages = useLessons();
  const instructors = useClubInstructors({ clubId });

  return {
    hasPackages: !!packages.result?.success && packages.lessons.length > 0,
    hasInstructors: !!instructors.result?.success && instructors.instructors.length > 0,
  };
}
